import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/api";
import { PILLARS, TIER_META, normalizeTier } from "@/constants";
import { useCategories } from "@/hooks/useCategories";

export default function BuyerDashboard() {
  const [data, setData] = useState(null);
  const CATEGORIES = useCategories();
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [period, setPeriod] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (category) params.category = category;
      if (period) params.period = period;
      const { data } = await api.get("/buyer/dashboard", { params });
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [category, period]);

  return (
    <div className="px-6 py-10 max-w-7xl mx-auto" data-testid="buyer-dashboard">
      <div className="mb-8">
        <p className="font-hand text-[var(--lala-blue)] text-2xl mb-1">vista comprador · líder de categoría</p>
        <h1 className="text-4xl font-extrabold text-[var(--ink)]">Desempeño de tus proveedores</h1>
        <p className="text-[var(--ink-soft)] mt-2">Ranking, comparativo por pilar, riesgo y nominaciones a reconocimiento.</p>
      </div>

      {/* Filters */}
      <div className="bg-white/70 rounded-2xl p-4 mb-8 flex gap-4 flex-wrap items-center border border-[var(--lala-blue)]/10">
        <div>
          <label className="text-xs font-semibold text-[var(--ink-soft)] uppercase block mb-1">Categoría</label>
          <select
            data-testid="buyer-filter-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="px-4 py-2 rounded-lg border border-[var(--lala-blue)]/30 bg-white"
          >
            <option value="">Todas</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-[var(--ink-soft)] uppercase block mb-1">Periodo (opcional)</label>
          <input
            data-testid="buyer-filter-period"
            type="text"
            placeholder="2026-02"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-4 py-2 rounded-lg border border-[var(--lala-blue)]/30 bg-white w-40"
          />
        </div>
        <button
          onClick={load}
          className="px-5 py-2 rounded-full bg-[var(--lala-blue)] text-white font-semibold hover:bg-[var(--lala-blue-deep)] mt-5"
        >
          Aplicar
        </button>
      </div>

      {loading ? (
        <div className="text-center py-20 font-hand text-2xl text-[var(--lala-blue)]">Cargando…</div>
      ) : !data ? null : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <KpiCard label="Proveedores" value={data.total_suppliers || 0} icon="fa-building" />
            <KpiCard label="En riesgo" value={data.risks?.length || 0} icon="fa-triangle-exclamation" tone="red" />
            <KpiCard label="Nominables" value={data.nominees?.length || 0} icon="fa-trophy" tone="gold" />
            <KpiCard label="Pilar más fuerte" value={pillarBest(data.pillar_avg)} icon="fa-star" tone="green" small />
          </div>

          {/* Pillar averages */}
          <section className="bg-white/70 rounded-2xl p-6 mb-8 border border-[var(--lala-blue)]/10">
            <h2 className="text-xl font-bold mb-4 text-[var(--ink)]">Promedio por pilar</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {PILLARS.map((p) => {
                const v = data.pillar_avg?.[p.key];
                const pct = v != null ? Math.round(v * 10) : 0;
                return (
                  <div key={p.key} className="p-4 rounded-xl bg-[var(--cream)] border border-[var(--lala-blue)]/10">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-[var(--ink)]">{p.label}</span>
                      <span className="font-extrabold text-[var(--lala-blue)] text-lg">{v != null ? v.toFixed(1) : "—"}</span>
                    </div>
                    <div className="w-full h-2 bg-white rounded-full overflow-hidden">
                      <div className="h-full bg-[var(--lala-blue)]" style={{ width: `${Math.min(pct, 100)}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Ranking */}
          <section className="bg-white/70 rounded-2xl p-6 mb-8 border border-[var(--lala-blue)]/10">
            <h2 className="text-xl font-bold mb-4 text-[var(--ink)]">Ranking de proveedores</h2>
            {data.ranking?.length === 0 ? (
              <p className="text-[var(--ink-soft)]">Sin proveedores con evaluación publicada.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="text-left text-xs text-[var(--ink-soft)] uppercase border-b border-[var(--lala-blue)]/10">
                    <tr><th className="py-3 pl-2">#</th><th>Proveedor</th><th>SAP ID</th><th>Categoría</th><th>Nivel</th><th>Score</th><th></th></tr>
                  </thead>
                  <tbody>
                    {data.ranking.map((r, i) => {
                      const tm = TIER_META[normalizeTier(r.tier)];
                      return (
                        <tr key={r.supplier_id} className="border-b border-[var(--lala-blue)]/5 hover:bg-[var(--cream)]/50">
                          <td className="py-3 pl-2 font-extrabold text-[var(--lala-blue)]">{i + 1}</td>
                          <td className="font-semibold">
                            {r.name}
                            {r.is_best_of_category && (
                              <i
                                className="fa-solid fa-trophy text-amber-500 ml-2"
                                title={data.score_badge_label || "Mejor de su Categoría"}
                                data-testid={`best-of-category-${r.supplier_id}`}
                              ></i>
                            )}
                          </td>
                          <td className="text-[var(--ink-soft)] text-sm">{r.sap_id || "—"}</td>
                          <td className="text-[var(--ink-soft)] text-sm">{r.category}</td>
                          <td><span className="px-3 py-1 rounded-full text-xs font-bold text-white" style={{ background: tm?.color }}>{tm?.label}</span></td>
                          <td>
                            <span className="font-extrabold text-lg">{(r.score_total || 0).toFixed(2)}</span>
                            <span className="block text-[10px] text-[var(--ink-soft)]">{r.score_100 ?? ((r.score_total || 0) * 10).toFixed(1)} /100</span>
                          </td>
                          <td><Link to={`/proveedores/${r.supplier_id}`} className="text-[var(--lala-blue)] font-semibold hover:underline">Ver →</Link></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Riesgos */}
          {data.risks?.length > 0 && (
            <section className="bg-red-50 rounded-2xl p-6 mb-8 border-2 border-red-200">
              <h2 className="text-xl font-bold mb-4 text-red-700">
                <i className="fa-solid fa-triangle-exclamation mr-2"></i>
                Proveedores que requieren plan de acción
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {data.risks.map((r) => (
                  <Link key={r.supplier_id} to={`/proveedores/${r.supplier_id}`} className="block p-4 rounded-xl bg-white border border-red-200 hover:shadow">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-[var(--ink)]">{r.name}</p>
                        <p className="text-xs text-[var(--ink-soft)]">{r.category} · {r.sap_id || "sin SAP"}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-extrabold text-red-700">{(r.score_total || 0).toFixed(1)}</p>
                        <p className="text-xs uppercase font-bold text-red-600">Riesgo {r.risk_level}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Nominees */}
          {data.nominees?.length > 0 && (
            <section className="bg-amber-50 rounded-2xl p-6 mb-8 border-2 border-amber-200">
              <h2 className="text-xl font-bold mb-4 text-amber-800">
                <i className="fa-solid fa-trophy mr-2"></i>
                Nominables a reconocimiento Lala Elite
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {data.nominees.map((r) => (
                  <Link key={r.supplier_id} to={`/proveedores/${r.supplier_id}`} className="block p-4 rounded-xl bg-white border border-amber-200 hover:shadow">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-[var(--ink)]">{r.name}</p>
                        <p className="text-xs text-[var(--ink-soft)]">{r.category}</p>
                      </div>
                      <p className="text-2xl font-extrabold text-amber-700">{(r.score_total || 0).toFixed(1)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function pillarBest(map) {
  if (!map) return "—";
  const items = Object.entries(map).filter(([_, v]) => v != null);
  if (!items.length) return "—";
  items.sort((a, b) => b[1] - a[1]);
  const p = PILLARS.find((x) => x.key === items[0][0]);
  return p ? p.label.split(" /")[0] : items[0][0];
}

function KpiCard({ label, value, icon, tone = "blue", small = false }) {
  const colors = {
    blue: { bg: "var(--lala-blue)", text: "white" },
    red: { bg: "var(--lala-red)", text: "white" },
    gold: { bg: "#D4A437", text: "white" },
    green: { bg: "var(--lala-green)", text: "white" },
  };
  const c = colors[tone];
  return (
    <div className="p-5 rounded-2xl shadow-sm" style={{ background: c.bg, color: c.text }}>
      <div className="flex items-center justify-between mb-2 opacity-80">
        <span className="text-xs uppercase font-bold tracking-wide">{label}</span>
        <i className={`fa-solid ${icon} text-xl`}></i>
      </div>
      <p className={small ? "text-2xl font-extrabold" : "text-4xl font-extrabold"}>{value}</p>
    </div>
  );
}
